// サムネイル向けに「文字があること」だけを伝える抽象表現（スケルトン帯）を描く。
//
// 背景: サムネイルは実キャンバスの 1/10 前後へ縮小して描くため、実文字は数 px 未満になって
// 読めない。従来はこれを避けるために font-size へ 6px の下限を置いていたが、その結果として
// 文字だけが本来の数倍〜十数倍の大きさで描かれ、図形の外へはみ出していた。
// 帯は文字サイズに比例した長方形なので下限が不要で、はみ出しが構造的に起きない。
//
// 行の割り出しは推定しない。表示・PDF 出力と同じ layoutMarkdownTextForCanvas を
// ctx.measureText を注入して呼び、折り返し後の 1 表示行ごとの Segment をそのまま帯にする。
// これにより帯の本数・長さ・寄せ・見出しの太さが実表示と一致する。
// 水平線の横罫と引用の左罫（Result.rects）も、同じレイアウト結果の矩形をそのまま細い帯として描く。
//
// 利用者の判断（2026-09-02）: サムネイル内の文字は実文字である必要はなく、
// 「そこに文字が書かれている」と分かる表現であればよい。

import type { TableColumnWidthsList } from "@refinement-canvas/domain";
import { layoutMarkdownTextForCanvas } from "./markdown-canvas-layout";
import { canvasFontFor } from "./canvas-sticky-text-metrics";

/** 帯の高さの、文字サイズに対する比。 */
const BAR_HEIGHT_RATIO = 0.5;
/** 帯の不透明度。下の塗りが透けて「文字の塊」に見える濃さ。 */
const BAR_ALPHA = 0.45;
/**
 * 1 図形あたりに描く帯の最大本数（DOM ではなく fillRect の呼び出し回数の上限）。
 *
 * 24 のような小さい値は置かない。描画領域に 25 行以上収まるテキスト・付箋で
 * 残りの帯が消え、「行数と行長が本物に対応する」という受け入れ条件に反するためである。
 * 実際の打ち切りは「描画領域の下端を超えた行で break」が担っており（下の
 * segment.baselineY > areaHeight）、本定数は異常時の上界にすぎない。
 *
 * 本文はスキーマ上 8,192 文字までで、1 行 1 文字でも 8,192 行が上界である。
 * レイアウト計算（layoutMarkdownTextForCanvas）は全 segments を先に生成するため
 * 本定数では計測コストの上限にならない——計測コストの上界は本文長（8,192 文字）が
 * 与えており、それは受け入れざるを得ない（表示側と PDF 出力側も同じ計算を通す）。
 */
const MAX_BARS = 8192;
/** 帯の角丸半径の、帯の高さに対する比。 */
const BAR_RADIUS_RATIO = 0.5;

export interface TextSkeletonOptions {
  /** text 図形の太字を幅計測へ反映する。 */
  readonly forceBold?: boolean | undefined;
  /** 生の Markdown テキスト（object.text）。マーカー除去はレイアウト側が行う。 */
  readonly text: string;
  /** 文字の描画領域（ラスタ px。図形矩形から余白と署名予約を引いた領域）。 */
  readonly areaWidth: number;
  readonly areaHeight: number;
  /** 描画領域の左上（ラスタ座標。ctx は図形ローカル座標へ変換済みである前提）。 */
  readonly originX: number;
  readonly originY: number;
  /** 基準文字サイズ（ラスタ px）。下限は設けない。 */
  readonly baseFontPx: number;
  /** 帯の色（表示側と同じ判定で解決した本文色）。 */
  readonly color: string;
  /** 行の寄せ。 */
  readonly align: "left" | "center" | "right";
  /**
   * 画面の CSS px をラスタ px へ換算する倍率（サムネイルの縮小率）。表の外枠線・区切り線の太さと
   * 使用可能幅の換算に使い、帯の列割りを実表示の列幅に対応させる。
   */
  readonly pxScale: number;
  /**
   * 本文中の表ごとの保存済み列幅（ブロック順）。渡さないとサムネイルの帯だけが自動列幅になり、
   * 実表示と違う列割り・折り返しで描かれる。
   */
  readonly tableColumnWidths?: TableColumnWidthsList | undefined;
}

/**
 * 文字のスケルトン帯を描く。呼び出し側で描画領域へのクリップを済ませておく。
 *
 * 帯 1 本 = 実レイアウトの 1 表示行。位置・幅・寄せ・文字サイズはレイアウト結果を
 * そのまま使うため、行数と行長が実表示に対応する（近似ではない）。
 */
export function drawTextSkeleton(
  ctx: CanvasRenderingContext2D,
  options: TextSkeletonOptions
): void {
  const { align, areaHeight, areaWidth, baseFontPx, color, originX, originY, pxScale, text } =
    options;
  if (areaWidth <= 0 || areaHeight <= 0 || baseFontPx <= 0 || text.length === 0) return;
  const layout = layoutMarkdownTextForCanvas({
    align,
    baseFontPx,
    maxWidth: areaWidth,
    forceBold: options.forceBold,
    measure: (segmentText, fontPx, font) => {
      ctx.font = canvasFontFor(fontPx, font);
      return ctx.measureText(segmentText).width;
    },
    pxScale,
    text,
    ...(options.tableColumnWidths === undefined
      ? {}
      : { tableColumnWidths: options.tableColumnWidths }),
  });
  ctx.save();
  ctx.globalAlpha = BAR_ALPHA;
  ctx.fillStyle = color;
  let drawn = 0;
  // 水平線の横罫・引用の左罫・表の外枠 / 列ヘッダー・コードブロックの面は、文字の帯と同じ色・
  // 不透明度の帯として文字より先に描く（実表示でも文字より背面）。矩形の位置・寸法はレイアウト結果を
  // そのまま使うため、帯と同じく縮小率に比例し、図形の外へはみ出さない。記法を含まない本文では rects が空。
  // 色は PDF のように役割（rule / quote-bar / table-* / code-bg / callout-*）ごとの実色へ解決しない。サムネイルは「そこに構造が
  // ある」ことだけを伝える抽象表現で、文字の帯も部分書式や引用の文字色を再現せず本文色にそろえて
  // いる。罫だけ実色にすると、明るい付箋では横罫（--color-border 相当）がほぼ見えなくなり、
  // 濃い付箋では本文色（白）の帯の方が構造を伝えるため、本文色で統一する。
  for (const rect of layout.rects) {
    if (drawn >= MAX_BARS) break;
    // 行ストライプは面積が大きく、本文色の帯で描くと文字の帯を覆うため、サムネイルでは描かない。
    // 縞は描画順の都合でヘッダより前に並ぶので、下端判定より先に読み飛ばす。
    if (
      rect.role === "table-body-bg" ||
      rect.role === "table-row-stripe" ||
      rect.role === "table-row-stripe-on-dark"
    )
      continue;
    // 描画領域の下端を超える罫は描かない（表示側も入り切らない行は見えない）。
    if (rect.y > areaHeight) break;
    // コードブロックの枠線は描かない。帯はすべて本文色 + 同じ不透明度で描く抽象表現のため、
    // 面（code-bg）と同じ外寸の枠線を重ねると縁だけ不透明度が二重に乗って濃くなるだけで、
    // 「そこにブロックがある」という情報は面の帯がすでに伝えている
    // （PDF は役割ごとの実色へ解決するため、面と枠線を描き分ける）。
    if (rect.role === "code-border") continue;
    // callout の背景は面積が大きく、本文色の帯で描くと中の文字の帯を塗りつぶしてしまう。
    // 左罫（callout-accent）だけを引用の左罫と同じ表現で描けば「囲みがある」ことは伝わる。
    // rect.fill（type ごとの実色）も使わない。サムネイルは「そこに構造がある」ことだけを伝える
    // 抽象表現で、文字の帯も罫も本文色へそろえているためである。
    if (rect.role === "callout-bg") continue;
    if (rect.w <= 0 || rect.h <= 0) continue;
    if (rect.role === "task-box") {
      const x = originX + rect.x;
      const y = originY + rect.y;
      const w = Math.min(areaWidth, rect.w);
      const thickness = Math.min(Math.max(1, 2 * pxScale), w, rect.h);
      ctx.fillRect(x, y, w, thickness);
      ctx.fillRect(x, y + rect.h - thickness, w, thickness);
      ctx.fillRect(x, y, thickness, rect.h);
      ctx.fillRect(x + w - thickness, y, thickness, rect.h);
      drawn += 4;
      continue;
    }
    ctx.fillRect(originX + rect.x, originY + rect.y, Math.min(areaWidth, rect.w), rect.h);
    drawn += 1;
  }
  for (const segment of layout.segments) {
    if (drawn >= MAX_BARS) break;
    // 描画領域の下端を超える行は描かない（表示側も入り切らない行は見えない）。
    if (segment.baselineY > areaHeight) break;
    if (segment.text.length === 0) continue;
    ctx.font = canvasFontFor(segment.fontPx, {
      bold: segment.bold,
      italic: segment.italic === true,
      monospace: segment.monospace === true,
    });
    // インラインコードの左右 padding は区間の占有幅に含まれる（レイアウトの送り幅と同じ）。
    // 帯の左端と幅から外すと、隣の帯との間に padding ぶんの隙間が空いてしまう。
    const padLeft = segment.padLeftPx ?? 0;
    const padRight = segment.padRightPx ?? 0;
    const barWidth = Math.min(areaWidth, ctx.measureText(segment.text).width + padLeft + padRight);
    if (barWidth <= 0) continue;
    const barHeight = segment.fontPx * BAR_HEIGHT_RATIO;
    // segment.x は anchor 基準の glyph 位置。padding を含む区間の左端へ直す。
    const left =
      segment.anchor === "center"
        ? segment.x - barWidth / 2
        : segment.anchor === "right"
          ? segment.x - barWidth
          : segment.x - padLeft;
    // baselineY はベースライン位置。帯はベースラインの上へ置く。
    const top = segment.baselineY - barHeight;
    fillRoundedRect(
      ctx,
      originX + left,
      originY + top,
      barWidth,
      barHeight,
      barHeight * BAR_RADIUS_RATIO
    );
    drawn += 1;
  }
  ctx.restore();
}

/**
 * 帯 1 本を描く（行を持たない単一行テキスト向け。セクションのタイトル・リンクカードの各行で使う）。
 * 幅・高さ・位置はいずれも呼び出し側が実測して渡す（本関数は Markdown レイアウトを通さない）。
 */
export function drawSingleTextBar(
  ctx: CanvasRenderingContext2D,
  bar: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
    readonly color: string;
  }
): void {
  if (bar.width <= 0 || bar.height <= 0) return;
  ctx.save();
  ctx.globalAlpha = BAR_ALPHA;
  ctx.fillStyle = bar.color;
  fillRoundedRect(ctx, bar.x, bar.y, bar.width, bar.height, bar.height * BAR_RADIUS_RATIO);
  ctx.restore();
}

/** 帯の高さ（文字サイズに対する比は本文の帯と共通）。 */
export function textBarHeightFor(fontPx: number): number {
  return fontPx * BAR_HEIGHT_RATIO;
}

/**
 * 表図形をサムネイル用の罫線と文字帯で描く。
 * ヘッダは面で塗らず、内容がある場合に行全体を代表する帯を 1 本だけ描く。
 */
export function drawTableSkeleton(
  ctx: CanvasRenderingContext2D,
  options: {
    /**
     * セルの**表示テキスト**（記法を解釈した後の、画面に見える文字列）。帯の幅をここから測るため、
     * 生テキストではなく表示テキストを渡す（記法の文字ぶん帯が長くなるのを防ぐ）。
     */
    readonly cellTexts: readonly (readonly string[])[];
    readonly columnWidths: readonly number[];
    readonly rowHeights: readonly number[];
    readonly borderWidth: number;
    readonly borderColor: string;
    readonly textColor: string;
    readonly fontPx: number;
    readonly paddingX: number;
  }
): void {
  // サムネイルの表外寸を、画面・PDF と同じ列幅・行高の合計から求める。
  const tableWidth = options.columnWidths.reduce((sum, value) => sum + value, 0);
  // 行方向も同じ解決済み寸法を合算し、外枠と列境界の描画範囲をそろえる。
  const tableHeight = options.rowHeights.reduce((sum, value) => sum + value, 0);
  ctx.save();
  ctx.fillStyle = options.borderColor;
  const border = Math.max(0.5, options.borderWidth);
  ctx.fillRect(0, 0, tableWidth + border * 2, border);
  ctx.fillRect(0, tableHeight + border, tableWidth + border * 2, border);
  ctx.fillRect(0, 0, border, tableHeight + border * 2);
  ctx.fillRect(tableWidth + border, 0, border, tableHeight + border * 2);
  let left = border;
  for (let column = 1; column < options.columnWidths.length; column += 1) {
    left += options.columnWidths[column - 1]!;
    ctx.fillRect(left, border, border, tableHeight);
  }

  const barHeight = textBarHeightFor(options.fontPx);
  let top = border;
  for (const [rowIndex, row] of options.cellTexts.entries()) {
    const rowHeight = options.rowHeights[rowIndex] ?? 0;
    if (rowIndex === 0) {
      const headerText = row.filter(Boolean).join(" ");
      if (headerText.length > 0) {
        ctx.font = `bold ${options.fontPx}px sans-serif`;
        drawSingleTextBar(ctx, {
          x: border + options.paddingX,
          y: top + Math.max(0, (rowHeight - barHeight) / 2),
          width: Math.min(
            Math.max(0, tableWidth - options.paddingX * 2),
            ctx.measureText(headerText).width
          ),
          height: barHeight,
          color: options.textColor,
        });
      }
    } else {
      left = border;
      for (const [columnIndex, cell] of row.entries()) {
        const columnWidth = options.columnWidths[columnIndex] ?? 0;
        if (cell.length > 0) {
          ctx.font = `${options.fontPx}px sans-serif`;
          drawSingleTextBar(ctx, {
            x: left + options.paddingX,
            y: top + Math.max(0, (rowHeight - barHeight) / 2),
            width: Math.min(
              Math.max(0, columnWidth - options.paddingX * 2),
              ctx.measureText(cell).width
            ),
            height: barHeight,
            color: options.textColor,
          });
        }
        left += columnWidth;
      }
    }
    top += rowHeight;
  }
  ctx.restore();
}

/** 角丸矩形を塗る。roundRect が無い環境では通常の矩形へ落ちる。 */
function fillRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): void {
  if (typeof ctx.roundRect !== "function" || radius <= 0.5) {
    ctx.fillRect(x, y, width, height);
    return;
  }
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, radius);
  ctx.fill();
}
